(() => {
  if (window.rfnGiveawayEntryInitialized) return;
  window.rfnGiveawayEntryInitialized = true;
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-rfn-scroll-entry]') : null;
    if (!trigger) return;
    const target = trigger.closest('.rfn-christmas')?.querySelector('#giveaway-entry');
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    const heading = target.querySelector('#entry-title');
    if (heading) heading.focus({preventScroll: true});
    const header = document.querySelector('sticky-header');
    const offset = header ? Math.min(header.getBoundingClientRect().height, 180) : 0;
    const top = Math.max(0, window.pageYOffset + target.getBoundingClientRect().top - offset - 20);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    try {
      window.scrollTo({top, left: 0, behavior});
    } catch {
      window.scrollTo(0, top);
    }
  }, true);
  const revision = '2026-10-15';
  const init = (form) => {
    if (form.dataset.initialized) return;
    form.dataset.initialized = 'true';
    const button = form.querySelector('[type="submit"]');
    const feedback = form.querySelector('[data-entry-feedback]');
    const retryButton = form.querySelector('[data-entry-retry]');
    const marketingInput = form.elements.marketing;
    const dialog = form.closest('dialog');
    const originalLabel = button.innerHTML;
    const zip = form.elements.zip;
    const firstNameInput = form.elements.first_name;
    const lastNameInput = form.elements.last_name;
    const emailInput = form.elements.email;
    let pending = false;
    let completed = false;
    let entrySaved = false;
    let subscription;
    let subscriptionController;
    let retryAvailableAt = 0;
    const active = () => form.isConnected && !document.hidden && (!dialog || dialog.open);
    const cancelSignup = () => subscriptionController?.abort();
    dialog?.addEventListener('close', cancelSignup);
    document.addEventListener('visibilitychange', () => { if (document.hidden) cancelSignup(); });
    window.addEventListener('pagehide', cancelSignup);
    const show = (text, status) => {
      feedback.hidden = false;
      feedback.textContent = text;
      feedback.dataset.status = status;
      if (active()) feedback.focus({ preventScroll: true });
    };
    const closed = () => Date.now() > Date.parse(form.dataset.closesAt);
    const post = async (endpoint, data, signal) => {
      const response = await fetch(`https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(form.dataset.companyId)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision },
        body: JSON.stringify({ data }),
        signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(15000)]) : AbortSignal.timeout(15000),
      });
      if (response.status !== 202) {
        const error = new Error('Entry service did not accept the request');
        error.transient = [408, 425, 429, 500, 502, 503, 504].includes(response.status);
        const retryAfter = response.headers.get('Retry-After');
        const delay = retryAfter && (/^\d+$/.test(retryAfter.trim()) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now());
        error.retryAfter = Number.isFinite(delay) ? Math.max(0, delay) : response.status === 429 ? 60000 : 0;
        throw error;
      }
    };
    const wait = (delay, signal) => new Promise((resolve, reject) => {
      const abort = () => { window.clearTimeout(timer); reject(signal.reason); };
      const timer = window.setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, delay);
      signal.addEventListener('abort', abort, {once: true});
      if (signal.aborted) abort();
    });
    const announce = (status, message) => {
      show(message, status);
      form.dispatchEvent(new CustomEvent('rfn:giveaway-entry', {bubbles: true, detail: {status, message, entryStatus: 'accepted', signupStatus: status === 'success' ? 'accepted' : 'unconfirmed'}}));
    };
    const signUp = async () => {
      subscriptionController = new AbortController();
      const {signal} = subscriptionController;
      const deadline = Date.now() + 60000;
      form.dataset.signupStatus = 'pending';
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (!active() || signal.aborted) throw new Error('Signup interrupted');
        try {
          await post('subscriptions', subscription, signal);
          return;
        } catch (error) {
          const transient = error.transient === true || error.name === 'TimeoutError' || error instanceof TypeError;
          const delay = Math.max(error.retryAfter || 0, 1000 * (2 ** attempt));
          retryAvailableAt = Math.max(retryAvailableAt, Date.now() + (error.retryAfter || 0));
          if (!transient || signal.aborted || !active() || attempt === 2 || Date.now() + delay + 15000 > deadline) throw error;
          show(form.dataset.signupRetrying, 'pending');
          await wait(delay, signal);
        }
      }
    };
    const finishSignup = async () => {
      try {
        await signUp();
        completed = true;
        form.dataset.signupStatus = 'accepted';
        announce('success', form.dataset.success);
      } catch {
        form.dataset.signupStatus = 'unconfirmed';
        marketingInput.checked = false;
        announce('partial', form.dataset.partial);
      } finally {
        pending = false;
        subscriptionController = null;
        form.setAttribute('aria-busy', 'false');
        form.querySelectorAll('input').forEach((input) => { input.disabled = true; });
        marketingInput.disabled = completed;
        button.hidden = true;
        retryButton.hidden = completed;
        retryButton.disabled = completed;
        retryButton.textContent = form.dataset.signupRetry;
      }
    };
    retryButton.addEventListener('click', async () => {
      if (pending || completed || !entrySaved || !active()) return;
      if (!marketingInput.reportValidity()) return;
      if (Date.now() < retryAvailableAt) { show(form.dataset.signupWait, 'partial'); return; }
      pending = true;
      form.setAttribute('aria-busy', 'true');
      marketingInput.disabled = true;
      retryButton.disabled = true;
      retryButton.textContent = form.dataset.signupBusy;
      show(form.dataset.signupBusy, 'pending');
      await finishSignup();
    });
    const checkZip = () => {
      zip.setCustomValidity('');
      if (zip.value.length === 5 && !/^(?:(83[2-8]|84[0-7])\d{2}|83120)$/.test(zip.value)) zip.setCustomValidity(form.dataset.zipError);
    };
    zip.addEventListener('input', checkZip);
    const checkName = () => [firstNameInput, lastNameInput].forEach(input => input.setCustomValidity(input.value.trim().length ? '' : form.dataset.nameError));
    firstNameInput.addEventListener('input', checkName);
    lastNameInput.addEventListener('input', checkName);
    const checkEmail = () => {
      emailInput.setCustomValidity('');
      const domain = emailInput.value.trim().toLowerCase().split('@').pop();
      if (/(^|\.)(example\.(com|net|org)|test\.com)$/.test(domain) || /\.(invalid|test|example|localhost)$/.test(domain)) emailInput.setCustomValidity(form.dataset.emailError);
    };
    emailInput.addEventListener('input', checkEmail);
    form.querySelectorAll('[data-open-rules]').forEach((link) => {
      link.addEventListener('click', () => {
        const rules = document.getElementById(link.dataset.openRules);
        if (rules) {
          rules.open = true;
          rules.scrollIntoView({block: 'nearest', behavior: 'instant'});
        }
      });
    });
    if (closed()) {
      show(form.dataset.closed, 'closed');
      button.disabled = true;
      return;
    }
    button.disabled = false;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || completed || entrySaved) return;
      checkZip();
      checkName();
      checkEmail();
      if (!form.reportValidity()) return;
      if (closed()) {
        show(form.dataset.closed, 'closed');
        button.disabled = true;
        return;
      }
      if (form.elements.website.value) return;
      pending = true;
      form.setAttribute('aria-busy', 'true');
      button.disabled = true;
      button.textContent = form.dataset.busy;
      feedback.hidden = true;
      const firstName = firstNameInput.value.trim().replace(/\s+/g, ' ');
      const lastName = lastNameInput.value.trim().replace(/\s+/g, ' ');
      const name = `${firstName} ${lastName}`;
      const email = form.elements.email.value.trim().toLowerCase();
      const zipCode = zip.value;
      const state = zipCode.startsWith('84') ? 'UT' : 'ID';
      const profile = {
        type: 'profile',
        attributes: {
          email,
          first_name: firstName,
          last_name: lastName,
          properties: { rfn_family_giveaway_2027: true },
        },
      };
      form.querySelectorAll('input').forEach((input) => { input.disabled = true; });
      try {
        const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${form.dataset.campaign}:${email}`));
        const entryId = Array.from(new Uint8Array(hash), (n) => n.toString(16).padStart(2, '0')).join('');
        Object.assign(profile.attributes.properties, {
          rfn_family_giveaway_2027_entry_id: entryId,
          rfn_family_giveaway_2027_zip: zipCode,
          rfn_family_giveaway_2027_rules_version: form.dataset.rulesVersion,
        });
        await post('events', {
          type: 'event',
          attributes: {
            unique_id: entryId,
            properties: {
              campaign_id: form.dataset.campaign,
              production_entry: true,
              entry_environment: 'production',
              entry_id: entryId,
              entrant_name: name,
              entrant_email: email,
              zip: zipCode,
              state,
              rules_accepted: true,
              age_18_or_older: true,
              eligible_region_attested: true,
              rules_version: form.dataset.rulesVersion,
              marketing_consent: true,
              source_url: `${window.location.origin}${window.location.pathname}`,
              entry_source: form.dataset.entrySource,
              promotion_name: 'RFN Christmas Giveaway',
              prize_package: 'SX-E8 + SX-E500',
              drawing_date: '2026-12-20',
            },
            metric: { data: { type: 'metric', attributes: { name: form.dataset.metric } } },
            profile: { data: profile },
          },
        });
        entrySaved = true;
        form.dataset.entryStatus = 'accepted';
        form.querySelectorAll('input').forEach((input) => { input.disabled = true; });
        subscription = {
          type: 'subscription',
          attributes: {
            custom_source: `RFN Christmas Giveaway / ${form.dataset.rulesVersion}`,
            profile: { data: { ...profile, attributes: { ...profile.attributes, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } },
          },
          relationships: { list: { data: { type: 'list', id: form.dataset.listId } } },
        };
        show(form.dataset.signupBusy, 'pending');
        await finishSignup();
      } catch {
        form.querySelectorAll('input').forEach((input) => { input.disabled = false; });
        show(form.dataset.error, 'error');
      } finally {
        pending = false;
        form.setAttribute('aria-busy', 'false');
        button.innerHTML = originalLabel;
        button.disabled = completed;
      }
    });
  };
  document.querySelectorAll('[data-rfn-entry-form]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-rfn-entry-form]').forEach(init));
  const openLinkedRules = () => {
    if (window.location.hash === '#giveaway-rules') {
      const rules = document.getElementById('giveaway-rules');
      if (rules) rules.open = true;
    }
  };
  window.addEventListener('hashchange', openLinkedRules);
  openLinkedRules();
})();

